import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import IndividualCard from '../../components/IndividualCard/IndividualCard';
import ContactUs from '../../components/ContactUs/ContactUs';
import styles from './ArmorOfGodPage.module.css';
import config from '../../config/config';
import { ArmorPostSanity, ArmorCardData } from '../../types/types';

const ArmorOfGodPage: React.FC = () => {
  const [armorPosts, setArmorPosts] = useState<ArmorCardData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to extract text from the first block of portable text
  const getPortableTextSummary = (portableTextBlocks: any): string => {
    if (!portableTextBlocks || !Array.isArray(portableTextBlocks) || portableTextBlocks.length === 0) {
      return 'No content available.';
    }
    
    const firstBlock = portableTextBlocks[0];
    if (firstBlock && firstBlock.children && Array.isArray(firstBlock.children) && firstBlock.children.length > 0) {
      return firstBlock.children
        .filter((child: any) => child.text)
        .map((child: any) => child.text)
        .join(' ');
    }
    
    return 'Content not in expected format.';
  };

  useEffect(() => {
    setLoading(true);
    
    // Fetch armor posts from Sanity.io
    const fetchArmorPosts = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/armor-posts`);
        
        if (response.data && response.data.length > 0) {
          // Sort fetched posts by date (newest first)
          const sortedPosts = response.data.sort((a: ArmorPostSanity, b: ArmorPostSanity) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          
          // Transform the posts to match the ArmorCardData interface
          const formattedPosts: ArmorCardData[] = sortedPosts.map((post: ArmorPostSanity) => ({
            id: post.id,
            title: post.title,
            date: post.date,
            contentSnippet: getPortableTextSummary(post.content),
            references: post.references || []
          }));
          
          setArmorPosts(formattedPosts);
          setError(null);
        } else {
          setError('No armor posts found.');
          setArmorPosts([]);
        }
      } catch (error) {
        console.error('Error fetching armor posts:', error);
        setError('Failed to fetch armor posts. Please try again later.');
        
        // Only use mock data if enabled in config
        if (config.USE_MOCK_DATA) {
          // Mock data would go here if needed
          const mockArmorPosts: ArmorCardData[] = [
            {
              id: 1,
              title: 'Belt of Truth - Ephesians 6:14a',
              date: '2025-03-17',
              contentSnippet: 'The belt of truth represents honesty, integrity, and a commitment to God\'s truth. With the truth, we will be able to understand the tricks Satan uses to water down the Word.',
              references: ['Ephesians 6:14a', 'John 17:17', 'Psalm 51:6', 'John 8:32']
            },
            {
              id: 2,
              title: 'Breastplate of Righteousness - Ephesians 6:14b',
              date: '2025-03-18',
              contentSnippet: 'The breastplate protects vital organs like the heart. Moral character serves as protection from spiritual enemies. Righteousness is a covering that guards our heart and soul.',
              references: ['Ephesians 6:14b', '2 Corinthians 5:21', 'Proverbs 28:1', 'Proverbs 4:23']
            },
            {
              id: 3,
              title: 'Sword of the Spirit - Ephesians 6:17b',
              date: '2025-03-22',
              contentSnippet: 'The sword of the Spirit is the Word of God. Just as a sword is an important piece of a soldier\'s equipment, the Word of God helps us understand the Spirit\'s leading and allows us to repel temptation when we learn it.',
              references: ['Ephesians 6:17b', 'Matthew 4:1-11', 'Hebrews 4:12', 'Psalm 119:11']
            }
          ];
          setArmorPosts(mockArmorPosts);
        } else {
          setArmorPosts([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArmorPosts();
  }, []);

  if (loading) return <div className={styles.loading}>Loading armor posts...</div>;
  if (error && armorPosts.length === 0) return <div className={styles.error}>{error}</div>;
  if (armorPosts.length === 0) return <div className={styles.empty}>No armor posts found.</div>;

  return (
    <div className={styles['armor-page']}>
      <section className={styles['armor-intro']}>
        <h1>Put on the Full Armor of God</h1>
        <p className={styles['armor-description']}>
          Ephesians 6:10-17 explains that we need to have the armor of God. This page explores what the parts
          of the full armor of God are and how they protect us in our spiritual battle.
        </p>
      </section>

      <section className={styles['armor-verses']}>
        <h2>The Pieces of God's Armor</h2>
        <p className={styles['click-instruction']}>Click on any armor piece to view its detailed teaching</p>
        <div className={styles['verses-container']}>
          {armorPosts.map((post) => (
            <Link 
              to={`/armor-of-god/${post.id}`}
              key={post.id}
              className={styles['verse-link']}
            >
              <div className={`${styles['verse-card']} ${styles['clickable']}`}>
                <IndividualCard
                  reference={post.title}
                  verses={post.contentSnippet}
                  date={post.date}
                />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <ContactUs />
    </div>
  );
};

export default ArmorOfGodPage;
